import { useEffect, type RefObject } from 'react'

interface UseDragToScrollOptions {
  threshold?: number
}

export function useDragToScroll(
  containerRef: RefObject<HTMLElement | null>,
  options: UseDragToScrollOptions = {}
): void {
  const { threshold = 5 } = options

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    let startX = 0
    let startY = 0
    let startScrollLeft = 0
    let startScrollTop = 0
    let isPanning = false
    let didPan = false
    let isPointerDown = false

    const onPointerDown = (e: PointerEvent): void => {
      if (e.pointerType === 'touch' || e.button !== 0 || !e.isPrimary) return

      const hasOverflow =
        container.scrollWidth > container.clientWidth ||
        container.scrollHeight > container.clientHeight
      if (!hasOverflow) return

      isPointerDown = true
      isPanning = false
      didPan = false

      startX = e.clientX
      startY = e.clientY
      startScrollLeft = container.scrollLeft
      startScrollTop = container.scrollTop
    }

    const onPointerMove = (e: PointerEvent): void => {
      if (!isPointerDown) return

      const dx = e.clientX - startX
      const dy = e.clientY - startY

      if (!isPanning) {
        if (Math.abs(dx) < threshold && Math.abs(dy) < threshold) return

        isPanning = true
        didPan = true
        container.style.cursor = 'grabbing'
        container.style.userSelect = 'none'

        container.setPointerCapture(e.pointerId)
      }

      container.scrollLeft = startScrollLeft - dx
      container.scrollTop = startScrollTop - dy
    }

    const onPointerUp = (e: PointerEvent): void => {
      if (!isPointerDown) return
      isPointerDown = false
      isPanning = false

      container.style.cursor = ''
      container.style.userSelect = ''

      if (container.hasPointerCapture(e.pointerId)) {
        container.releasePointerCapture(e.pointerId)
      }
    }

    const onClickCapture = (e: MouseEvent): void => {
      if (didPan) {
        e.stopPropagation()
        e.preventDefault()
        didPan = false
      }
    }

    container.addEventListener('pointerdown', onPointerDown)
    container.addEventListener('pointermove', onPointerMove)
    container.addEventListener('pointerup', onPointerUp)
    container.addEventListener('pointercancel', onPointerUp)
    container.addEventListener('click', onClickCapture, true)

    return () => {
      container.removeEventListener('pointerdown', onPointerDown)
      container.removeEventListener('pointermove', onPointerMove)
      container.removeEventListener('pointerup', onPointerUp)
      container.removeEventListener('pointercancel', onPointerUp)
      container.removeEventListener('click', onClickCapture, true)

      container.style.cursor = ''
      container.style.userSelect = ''
    }
  }, [containerRef, threshold])
}
